<script>
import { createNamespacedHelpers } from "vuex";
import BaseTileGridLoader from "@/components/BaseTileGridLoader";
import ProductTile from "@/components/products/ProductTile";
import ProductPagination from "@/components/products/ProductPagination";

const { mapGetters, mapMutations } = createNamespacedHelpers("products");

/** The product listing output grid. This component displays Product Tile components in a paginated format and
 * includes a loading animation before all of the products are loaded into the app via Vuex */

export default {
  name: "ProductTileGrid",

  components: {
    BaseTileGridLoader,
    ProductTile,
    ProductPagination,
  },

  props: {
    /** Whether or not to hide the label "Price on Request" **/
    hidePriceOnRequestLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Whether or not to hide the price line all together. **/
    hidePrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    destination: {
      type: String,
      default: "",
    },
    moreWidth: {
      type: Boolean,
      default: false,
    },
    ctaWebsite: {
      type: String,
      default: null,
    },
    ctaBrandName: {
      type: String,
      default: null,
    },
     ctaMessage: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      loader: {
        width: 0,
        height: 0,
      },
      isLoaded: false,
    };
  },

  computed: {
    /** Vuex getter methods */
    ...mapGetters([
      "getPaginatedProducts",
      "getIsLoading",
      "getFirstLoad",
      "getBrowsingSnapshot",
      "getPaginationData",
      "isLastPage",
    ]),
    /** Returns the loaders emitted height */
    loaderHeight: function () {
      return this.loader.height;
    },
    /** Create the loader CSS style height string */
    loaderStyles: function () {
      return "height:" + this.loaderHeight + "px;";
    },
  },

  watch: {
    getIsLoading(item) {
      if (!item) {
        if (this.getPaginatedProducts.length === 0) {
          this.isLoaded = true;
        }
      }
    },
  },

  updated() {
    this.scrollToSnapshotPosition();
  },

  methods: {
    /** Vuex mutations */
    ...mapMutations(["setFirstLoad"]),
    /** Sets the loader components height */
    setLoaderHeight(loader) {
      this.loader.height = loader.height;
    },
    /** Scrolls the page to the last snapshot position in local storage */
    scrollToSnapshotPosition() {
      if (this.getFirstLoad && this.getBrowsingSnapshot) {
        const yPos = this.getBrowsingSnapshot.scrollPosition;
        window.scrollTo(0, yPos);
        this.setFirstLoad(false);
      }
    },
  },
};
</script>

<template>
  <div
    class="relative py-6 md:col-span-2 lg:col-span-3"
    :class="`${moreWidth ? 'pl-0 ml-0 lg:ml-1 lg:pl-1' : 'pl-0 ml-0 lg:pl-6'}`"
    :style="getIsLoading ? 'height: ' + (loaderHeight + 48) + 'px' : ''"
  >
    <transition name="fade">
      <div v-if="getIsLoading" class="absolute z-10 || loader" :style="loaderStyles">
        <BaseTileGridLoader @resize="setLoaderHeight" />
      </div>
    </transition>

    <transition name="fade">
      <div v-if="!getIsLoading">
        <section v-if="getPaginatedProducts.length">
          <div
            class="grid grid-cols-2 gap-x-4 gap-y-8 | md:grid-cols-3 | lg:grid-cols-4"
            role="feed"
          >
            <ProductTile
              v-for="product in getPaginatedProducts"
              :key="product.id"
              v-bind="product"
              :destination="destination"
              :hide-price="hidePrice"
            />

            <!-- Only show on last page if both ctaWebsite and ctaBrandName are provided -->
            <div
              v-if="isLastPage && ctaWebsite"
              class="cta-tile-custom-wrap block relative"
            >
              <article
                class="cta-tile-custom focus:outline-none anchor cursor-pointer relative flex items-center justify-center text-center p-6 bg-black"
              >
                <a
                  class="cta-tile-custom-text focus:outline-none anchor text-white text-center font-cormorant font-bold"
                  :href="ctaWebsite"
                   target="_blank"
                   rel="noopener noreferrer"
                   v-html="ctaMessage || `See the Full <br /> ${ctaBrandName} Collection Here`"
                >                 
                </a>
              </article>
            </div>
          </div>
          <ProductPagination
            class="mt-4 | md:mt-6"
            :count="getPaginatedProducts.length"
          />
        </section>

        <div v-if="isLoaded" class="p-4 text-center | md:p-6">
          <h3 class="mb-2 font-bold text-lg">No Products Found</h3>
          <p>Please try again by widening your search and filter settings.</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="postcss">
.loader {
  top: 16px;
  right: 16px;
  left: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.cta-tile-custom {
  aspect-ratio: 1/1;
}

.cta-tile-custom-text {
  font-size: 2.25rem;
  line-height: 43px;
}

@screen md {
  .loader {
    top: 24px;
    right: 24px;
    left: 24px;
  }
}
</style>